<script setup>
import getUuid from 'widget/helpers/uuid';
import { computed, ref, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import WaveSurfer from 'wavesurfer.js';
import RecordPlugin from 'wavesurfer.js/dist/plugins/record.js';
import { convertAudio } from './utils/audioConversionUtils';

const props = defineProps({
  isSendDisabled: { type: Boolean, default: false },
  audioRecordFormat: {
    type: String,
    required: true,
  },
});

const emit = defineEmits([
  'recorderProgressChanged',
  'finishRecord',
  'pause',
  'play',
  'recordError',
  'discard',
  'send',
]);

const { t } = useI18n();
// These keys come only from the recorder controls below.
// eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
const copy = key => t(`CONVERSATION.REPLYBOX.AUDIO_RECORDER.${key}`);

const waveformContainer = ref(null);
const wavesurfer = ref(null);
const record = ref(null);
const isRecording = ref(false);
const isPlaying = ref(false);
const hasRecording = ref(false);
const recordedAudioUrl = ref(null);
const duration = ref('00:00');
let disposed = false;
const statusLabel = computed(() => {
  if (isRecording.value) return copy('RECORDING');
  if (hasRecording.value) return copy(isPlaying.value ? 'PLAYING' : 'READY');
  return copy('PREPARING');
});

const formatTimeProgress = time => {
  const seconds = Math.floor(time / 1000);
  return `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;
};

const AUDIO_EXTENSION_MAP = {
  'audio/ogg': 'ogg',
  'audio/mp3': 'mp3',
  'audio/mpeg': 'mp3',
  'audio/wav': 'wav',
  'audio/webm': 'webm',
};

const getRecordPluginOptions = audioFormat => {
  const options = {
    scrollingWaveform: true,
    renderRecordedAudio: false,
  };
  if (
    audioFormat === 'audio/ogg' &&
    MediaRecorder.isTypeSupported('audio/ogg;codecs=opus')
  ) {
    options.mimeType = 'audio/ogg;codecs=opus';
  }
  return options;
};

const initWaveSurfer = () => {
  wavesurfer.value = WaveSurfer.create({
    container: waveformContainer.value,
    waveColor: '#8195a8',
    progressColor: '#0891b2',
    height: 44,
    barWidth: 2,
    barGap: 1,
    barRadius: 2,
    plugins: [
      RecordPlugin.create(getRecordPluginOptions(props.audioRecordFormat)),
    ],
  });

  wavesurfer.value.on('pause', () => {
    isPlaying.value = false;
    emit('pause');
  });
  wavesurfer.value.on('play', () => {
    isPlaying.value = true;
    emit('play');
  });

  record.value = wavesurfer.value.plugins[0];

  wavesurfer.value.on('finish', () => {
    isPlaying.value = false;
  });

  record.value.on('record-end', async blob => {
    if (disposed) return;
    try {
      const audioBlob = await convertAudio(blob, props.audioRecordFormat);
      if (disposed) return;
      // Use the converted blob's actual type, which may differ from the
      // requested format when the browser can't produce it (e.g. Safari falls
      // back to MP3 instead of OGG). This keeps the filename, content type, and
      // voice-note flag consistent with the real bytes.
      const audioType = audioBlob.type || props.audioRecordFormat;
      const ext = AUDIO_EXTENSION_MAP[audioType] || 'mp3';
      const fileName = `${getUuid()}.${ext}`;
      const file = new File([audioBlob], fileName, {
        type: audioType,
      });
      if (recordedAudioUrl.value) URL.revokeObjectURL(recordedAudioUrl.value);
      recordedAudioUrl.value = URL.createObjectURL(audioBlob);
      await wavesurfer.value.load(recordedAudioUrl.value);
      if (disposed) return;
      emit('finishRecord', {
        name: file.name,
        type: file.type,
        size: file.size,
        file,
      });
      hasRecording.value = true;
      isRecording.value = false;
    } catch (error) {
      if (disposed) return;
      isRecording.value = false;
      hasRecording.value = false;
      emit('recordError', { error });
    }
  });

  record.value.on('record-progress', time => {
    duration.value = formatTimeProgress(time);
    emit('recorderProgressChanged', duration.value);
  });
};

const stopRecording = () => {
  if (isRecording.value) {
    record.value.stopRecording();
    isRecording.value = false;
  }
};

const startRecording = async () => {
  try {
    await record.value.startRecording();
    if (disposed) {
      record.value.stopMic();
      return;
    }
    isRecording.value = true;
  } catch (error) {
    if (!disposed) emit('recordError', { error });
  }
};

const playPause = () => {
  if (hasRecording.value) {
    wavesurfer.value.playPause();
  }
};

onMounted(() => {
  initWaveSurfer();
  startRecording();
});

onUnmounted(() => {
  disposed = true;
  if (recordedAudioUrl.value) {
    URL.revokeObjectURL(recordedAudioUrl.value);
    recordedAudioUrl.value = null;
  }
  if (wavesurfer.value) {
    wavesurfer.value.destroy();
  }
});

defineExpose({ playPause, stopRecording, record });
</script>

<template>
  <section class="audio-recorder" :aria-label="copy('TITLE')">
    <div class="audio-recorder__header">
      <span
        class="audio-recorder__mic"
        :class="{ 'is-recording': isRecording }"
        aria-hidden="true"
      >
        <i class="i-hugeicons-mic-01 size-5" />
      </span>
      <div class="min-w-0 flex-1">
        <p class="mb-0 text-sm font-semibold text-n-slate-12">
          {{ copy('TITLE') }}
        </p>
        <p class="mb-0 text-xs text-n-slate-11" role="status">
          {{ statusLabel }}
        </p>
      </div>
      <span
        class="audio-recorder__duration"
        :class="{ 'text-n-ruby-11': isRecording }"
        >{{ duration }}</span
      >
    </div>
    <div class="audio-recorder__wave">
      <div ref="waveformContainer" class="min-w-0 w-full" />
    </div>
    <div class="audio-recorder__actions">
      <Button
        :label="copy('DISCARD')"
        icon="i-hugeicons-delete-02"
        color="slate"
        variant="ghost"
        size="sm"
        @click="emit('discard')"
      />
      <div class="flex flex-wrap items-center gap-2">
        <Button
          v-if="isRecording"
          :label="copy('STOP')"
          icon="i-hugeicons-stop"
          color="ruby"
          variant="faded"
          size="sm"
          @click="stopRecording"
        />
        <Button
          v-else
          :label="copy(isPlaying ? 'PAUSE' : 'LISTEN')"
          :icon="isPlaying ? 'i-hugeicons-pause' : 'i-hugeicons-play'"
          color="slate"
          variant="faded"
          size="sm"
          :disabled="!hasRecording"
          @click="playPause"
        />
        <Button
          :label="copy('SEND')"
          icon="i-hugeicons-sent"
          color="blue"
          size="sm"
          :disabled="!hasRecording || isSendDisabled"
          @click="emit('send')"
        />
      </div>
    </div>
  </section>
</template>

<style scoped lang="scss">
.audio-recorder {
  @apply rounded-2xl border border-n-weak bg-n-solid-2 p-3;
}
.audio-recorder__header {
  @apply flex items-center gap-3;
}
.audio-recorder__mic {
  @apply grid size-9 shrink-0 place-items-center rounded-xl bg-n-blue-3 text-n-blue-11;
  &.is-recording {
    @apply bg-n-ruby-3 text-n-ruby-11;
  }
}
.audio-recorder__duration {
  @apply shrink-0 rounded-lg bg-n-alpha-2 px-2.5 py-1 font-mono text-sm font-semibold tabular-nums text-n-slate-12;
}
.audio-recorder__wave {
  @apply my-3 min-w-0 overflow-hidden rounded-lg bg-n-alpha-1 px-2 py-1;
}
.audio-recorder__actions {
  @apply flex flex-wrap items-center justify-between gap-2 border-t border-n-weak pt-3;
}
</style>
